/**
 * 买家端 i18n 字典：key → { zh, en }，韩文在 ko.ts（按同一套 key，类型上强制补全）。
 * 所有界面文案统一放这里，组件内用 t(key, vars?) / tr(key, vars?) 取值，支持 {name} 占位符。
 * 新增文案：在此加 zh/en，再到 ko.ts 加韩文（缺韩文会编译报错）。
 */
export type Lang = 'zh' | 'en' | 'ko';

export type Entry = { zh: string; en: string };

const ENTRIES = {
  'app.name': { zh: 'Bluplanet', en: 'Bluplanet' },

  'home.title': { zh: '首页', en: 'Home' },
  'home.publishRfq': { zh: '发布询盘', en: 'Publish RFQ' },
  'home.publishRfqDesc': { zh: '一键发布多品采购需求，贸易顾问极速报价', en: 'Post multi-item requests, get quotes fast' },
  'home.hot': { zh: '当季主推', en: 'Seasonal Picks' },
  'home.viewDetail': { zh: '查看详情', en: 'View Details' },
  'home.search': { zh: '搜索农产品、产地、品类', en: 'Search produce, origin, category' },
  'home.selected': { zh: '甄选农产品', en: 'Selected Produce' },
  'home.workbench': { zh: '我的工作台', en: 'My Workspace' },
  'home.noMatch': { zh: '未找到匹配的商品', en: 'No matching products' },
  'home.categoryAll': { zh: '全部', en: 'All' },
  'banner.fallbackTitle': { zh: '全球优质农产品直达', en: 'Quality Agri-Products, Delivered' },
  'banner.fallbackSubtitle': { zh: '产地直采 · 冷链全程可视 · 贸易顾问极速报价', en: 'Direct from origin · Cold-chain visibility · Fast quotes' },
  'profile.title': { zh: '完善企业信息', en: 'Complete Your Profile' },
  'profile.desc': { zh: '补充公司信息便于贸易顾问为您提供更精准的报价', en: 'Add company info for more accurate quotes' },
  'profile.company': { zh: '公司名称', en: 'Company Name' },
  'profile.contact': { zh: '联系人', en: 'Contact Person' },
  'profile.country': { zh: '国家/地区', en: 'Country/Region' },
  'profile.phone': { zh: '联系电话', en: 'Contact Phone' },
  'profile.companyRequired': { zh: '请填写公司名称', en: 'Company name is required' },
  'profile.saveFailed': { zh: '保存失败，请稍后重试', en: 'Save failed, please retry' },
  'profile.skip': { zh: '稍后再说', en: 'Later' },
  'profile.save': { zh: '保存', en: 'Save' },
  'profile.changePassword': { zh: '修改密码', en: 'Change Password' },
  'profile.changePwdTitle': { zh: '修改登录密码', en: 'Change Password' },
  'profile.changePwdDesc': { zh: '为了账户安全，建议定期更换登录密码。', en: 'For account security, we recommend changing your password regularly.' },
  'profile.oldPassword': { zh: '原密码', en: 'Current Password' },
  'profile.newPassword': { zh: '新密码', en: 'New Password' },
  'profile.confirmPassword': { zh: '确认新密码', en: 'Confirm New Password' },
  'profile.oldPwdRequired': { zh: '请输入原密码', en: 'Please enter your current password' },
  'profile.newPwdMin': { zh: '新密码至少 6 位', en: 'New password must be at least 6 characters' },
  'profile.pwdMismatch': { zh: '两次输入的新密码不一致', en: 'The new passwords do not match' },
  'profile.pwdSame': { zh: '新密码不能与当前密码相同', en: 'New password must differ from the current one' },
  'profile.pwdChanged': { zh: '密码修改成功', en: 'Password changed successfully' },
  'profile.entry': { zh: '完善企业信息', en: 'Complete Profile' },
  'profile.todo': { zh: '待完善', en: 'Incomplete' },

  'inquiry.title': { zh: '询盘', en: 'Inquiry' },
  'inquiry.items': { zh: '询盘商品', en: 'Items' },
  'inquiry.addItem': { zh: '添加商品行', en: 'Add item' },
  'inquiry.removeItem': { zh: '删除该行', en: 'Remove' },
  'inquiry.bindSpu': { zh: '关联商品（可选，留空则自由描述）', en: 'Link product (optional; leave blank for free text)' },
  'inquiry.productName': { zh: '品名', en: 'Product name' },
  'inquiry.spec': { zh: '规格', en: 'Specification' },
  'inquiry.packaging': { zh: '包装', en: 'Packaging' },
  'inquiry.quantity': { zh: '数量', en: 'Quantity' },
  'inquiry.unit': { zh: '单位', en: 'Unit' },
  'inquiry.tradeTerm': { zh: '贸易术语', en: 'Trade term' },
  'inquiry.destCountry': { zh: '目的国', en: 'Destination country' },
  'inquiry.destPort': { zh: '目的港', en: 'Destination port' },
  'inquiry.destFree': { zh: '目的地（自由文本兜底）', en: 'Destination (free text)' },
  'inquiry.expectDate': { zh: '期望交期', en: 'Expected delivery' },
  'inquiry.message': { zh: '补充说明（规格、交期、目的港等）', en: 'Remarks (spec, ETD, port, etc.)' },
  'inquiry.submit': { zh: '提交询盘', en: 'Submit inquiry' },
  'inquiry.save': { zh: '保存修改', en: 'Save changes' },
  'inquiry.withdraw': { zh: '撤回', en: 'Withdraw' },
  'inquiry.withdrawTitle': { zh: '撤回询盘', en: 'Withdraw inquiry' },
  'inquiry.withdrawConfirm': { zh: '撤回后该询盘将不再被后台处理，且不可恢复。确定撤回吗？', en: 'The inquiry will no longer be processed and cannot be restored. Withdraw it?' },
  'inquiry.cancel': { zh: '取消', en: 'Cancel' },
  'inquiry.updateFailed': { zh: '保存失败，请稍后重试', en: 'Save failed, please retry' },
  'inquiry.loadFailed': { zh: '加载询盘失败，请返回列表重试', en: 'Failed to load inquiry, please go back and retry' },
  'inquiry.statusWithdrawn': { zh: '已撤回', en: 'Withdrawn' },
  'inquiry.submitted': { zh: '询盘已提交', en: 'Inquiry submitted' },
  'inquiry.submittedDesc': { zh: '贸易顾问将尽快回复正式报价，您可在「我的询盘」中查看进度。', en: 'Our trade advisor will reply with a formal quote shortly. Track it under "My Inquiries".' },
  'inquiry.viewMine': { zh: '查看我的询盘', en: 'View my inquiries' },
  'inquiry.needLogin': { zh: '请先登录买家账号后再发起询盘', en: 'Please log in before submitting an inquiry' },
  'inquiry.qtyPositive': { zh: '每个商品行数量必须大于 0', en: 'Each item quantity must be greater than 0' },
  'inquiry.atLeastOne': { zh: '询盘至少包含一个商品行', en: 'At least one item is required' },
  'inquiry.category': { zh: '品类', en: 'Category' },
  'inquiry.categoryNew': { zh: '+ 新增品类', en: '+ New category' },
  'inquiry.productSearch': { zh: '商品名称（点击列出商品，或输入名称搜索）', en: 'Product name (pick from list, or type to search)' },
  'inquiry.rowsTitle': { zh: '需求行（报价逐行对应）', en: 'Requirement rows (quoted per row)' },
  'inquiry.rowPrefix': { zh: '第 ', en: 'Row ' },
  'inquiry.rowSuffix': { zh: ' 行', en: '' },
  'inquiry.pickSku': { zh: '商品 / 规格（SKU）', en: 'Product / Spec (SKU)' },
  'inquiry.pickSkuBtn': { zh: '点击选择商品档案', en: 'Pick from catalog' },
  'inquiry.edit': { zh: '更改', en: 'Change' },
  'inquiry.quantityTon': { zh: '数量（吨）', en: 'Quantity (t)' },
  'inquiry.innerPack': { zh: '内包装', en: 'Inner packaging' },
  'inquiry.outerPack': { zh: '外包装', en: 'Outer packaging' },
  'inquiry.pallet': { zh: '是否含托盘', en: 'Palletized' },
  'inquiry.palletYes': { zh: '含托盘', en: 'Palletized' },
  'inquiry.palletNo': { zh: '不含托盘', en: 'No pallet' },
  'inquiry.addRow': { zh: '+ 增加一行', en: '+ Add row' },
  'inquiry.packOther': { zh: '+', en: '+' },
  'inquiry.packEditCustom': { zh: '点击修改自定义包装', en: 'Click to edit custom packaging' },
  'inquiry.packCustom': { zh: '自定义包装', en: 'Custom packaging' },
  'inquiry.pickConfirm': { zh: '选择', en: 'Select' },
  'inquiry.pickedCount': { zh: '等 {n} 个商品', en: '+{n} products' },
  'inquiry.pickEmpty': { zh: '该品类下暂无商品档案，可直接在商品名称输入自定义需求', en: 'No products in this category; type a custom request instead' },
  'inquiry.rowNeedsProduct': { zh: '每行请先选择商品档案或填写自定义需求', en: 'Pick a catalog product or enter a custom request for each row' },
  'inquiry.pickSearchPh': { zh: '输入名称/规格/编码过滤，留空显示全部', en: 'Filter by name/spec/code; blank shows all' },
  'inquiry.pickCustom': { zh: '按自定义名称录入', en: 'Enter as custom name' },
  'inquiry.pickAllSku': { zh: '选择商品档案', en: 'Pick from catalog' },
  'inquiry.innerNetWeight': { zh: '内包装每件净重（g，选填）', en: 'Net weight per inner pack (g, optional)' },
  'inquiry.outerNetWeight': { zh: '外包装每箱净重（kg，选填）', en: 'Net weight per carton (kg, optional)' },
  'inquiry.innerNetPh': { zh: '如 500，即每袋 500g', en: 'e.g. 500 = 500g per bag' },
  'inquiry.outerNetPh': { zh: '如 10，即每箱 10kg', en: 'e.g. 10 = 10kg per carton' },

  'register.title': { zh: '注册买家账号', en: 'Create buyer account' },
  'register.email': { zh: '邮箱', en: 'Email' },
  'register.code': { zh: '验证码', en: 'Verification code' },
  'register.sendCode': { zh: '获取验证码', en: 'Get code' },
  'register.resend': { zh: '重新获取', en: 'Resend' },
  'register.password': { zh: '密码', en: 'Password' },
  'register.confirmPwd': { zh: '确认密码', en: 'Confirm password' },
  'register.company': { zh: '公司名称', en: 'Company' },
  'register.contact': { zh: '联系人', en: 'Contact name' },
  'register.country': { zh: '国家', en: 'Country' },
  'register.submit': { zh: '注册并登录', en: 'Sign up' },
  'register.haveAccount': { zh: '已有账号？去登录', en: 'Have an account? Sign in' },
  'register.codePh': { zh: '6 位数字', en: '6-digit code' },
  'register.emailRequired': { zh: '请输入邮箱', en: 'Email is required' },
  'register.emailInvalid': { zh: '邮箱格式不正确', en: 'Invalid email format' },
  'register.code6': { zh: '请输入 6 位验证码', en: 'Enter the 6-digit code' },
  'register.sendFailed': { zh: '验证码发送失败，请稍后重试', en: 'Failed to send code, please retry' },
  'register.failed': { zh: '注册失败，请重试', en: 'Registration failed, please retry' },
  'register.failedRetry': { zh: '注册失败，请稍后重试', en: 'Registration failed, please try again later' },

  // ---- 登录页 ----
  'login.welcome': { zh: '欢迎登录', en: 'Welcome back' },
  'login.subtitle': { zh: '买家履约统一入口，请使用注册邮箱登录', en: 'Unified buyer portal — sign in with your registered email' },
  'login.email': { zh: '邮箱', en: 'Email' },
  'login.emailPh': { zh: '请输入登录邮箱', en: 'Enter your email' },
  'login.pwd': { zh: '密码', en: 'Password' },
  'login.pwdPh': { zh: '请输入密码', en: 'Enter your password' },
  'login.remember': { zh: '记住我', en: 'Remember me' },
  'login.submit': { zh: '登 录', en: 'Sign in' },
  'login.toRegister': { zh: '还没有账号？立即注册', en: "Don't have an account? Sign up" },
  'login.errEmpty': { zh: '请输入邮箱和密码', en: 'Please enter your email and password' },
  'login.errFail': { zh: '登录失败，请检查账号或密码', en: 'Sign-in failed. Please check your account or password' },
  'login.errRetry': { zh: '登录失败，请稍后重试', en: 'Sign-in failed, please try again later' },
  'login.featuresTitle': { zh: 'Bluplanet 核心能力', en: 'Why Bluplanet' },
  'login.brandDesc': { zh: '农产品跨境贸易的全程伙伴，从产地到您手中，每一步都有保障。', en: 'Your end-to-end partner for cross-border agricultural trade. From origin to your door, with guarantee at every step.' },
  'login.f1t': { zh: '端到端履约', en: 'End-to-End Fulfillment' },
  'login.f1d': { zh: '产地直采 · 冷链交付', en: 'Direct sourcing · cold-chain delivery' },
  'login.f2t': { zh: '实时 IoT 追踪', en: 'Live IoT Tracking' },
  'login.f2d': { zh: '全程温湿度可视', en: 'Full-journey temperature visibility' },
  'login.f3t': { zh: '贸易信用保障', en: 'Trade Credit Backing' },
  'login.f3d': { zh: '中信保支持 · 单证存证', en: 'Sinosure backing · document custody' },
  'register.pwdMismatch': { zh: '两次输入的密码不一致', en: 'Passwords do not match' },
  'register.pwdTooShort': { zh: '密码至少 6 位', en: 'Password must be at least 6 characters' },
  'register.codeSent': { zh: '验证码已发送（60 秒内有效）', en: 'Code sent (valid 60s)' },
  'register.sending': { zh: '发送中…', en: 'Sending…' },

  'common.backHome': { zh: '返回首页', en: 'Back to home' },
  'common.lang': { zh: 'EN', en: '中' },
  'common.currency': { zh: '币种', en: 'Currency' },

  // ---- 营销站首页（顶导航 / Hero / 信任带 / 服务保障 / 页脚 / 移动 Tab） ----
  'nav.home': { zh: '首页', en: 'Home' },
  'nav.products': { zh: '全部商品', en: 'Products' },
  'nav.services': { zh: '服务保障', en: 'Services' },
  'nav.tabInquiry': { zh: '询盘', en: 'RFQ' },
  'nav.login': { zh: '登录', en: 'Sign in' },
  'nav.register': { zh: '注册', en: 'Sign up' },
  'nav.myOrders': { zh: '我的订单', en: 'My Orders' },
  'nav.profile': { zh: '个人中心', en: 'Profile' },
  'nav.myInquiries': { zh: '我的询盘', en: 'My Inquiries' },
  'nav.myClaims': { zh: '我的理赔', en: 'My Claims' },
  'nav.messages': { zh: '消息中心', en: 'Messages' },
  'nav.logout': { zh: '退出登录', en: 'Sign out' },
  'nav.ai': { zh: '在线咨询', en: 'Live Chat' },
  // ---- 布局页头标题 / 底部 Tab（覆盖 useTitle 与 BottomTabBar）----
  'nav.ordersCenter': { zh: '订单中心', en: 'Orders' },
  'nav.orderDetail': { zh: '订单详情', en: 'Order Details' },
  'nav.productDetail': { zh: '商品详情', en: 'Product Details' },
  'nav.buyNow': { zh: '立即购买', en: 'Buy Now' },
  'nav.tabOrders': { zh: '订单', en: 'Orders' },
  'nav.tabMe': { zh: '我的', en: 'Me' },
  'common.back': { zh: '返回', en: 'Back' },
  'hero.label': { zh: '产地直采 · 冷链全程可视', en: 'DIRECT FROM ORIGIN · COLD-CHAIN VISIBLE' },
  'hero.title1': { zh: '全球优质农产品', en: 'Quality Agri-Products,' },
  'hero.title2': { zh: '从产地直达您的手中', en: 'From Origin to Your Door' },
  'hero.desc': { zh: '单证合规 · 贸易顾问 1 对 1 报价 · 17 节点履约全程可视', en: 'Compliant docs · 1-on-1 advisor · 17-node fulfillment visibility' },
  'hero.cta1': { zh: '发布询盘', en: 'Publish RFQ' },
  'hero.cta2': { zh: '浏览商品', en: 'Browse Products' },
  'trust.ports': { zh: '目的港', en: 'Destination Ports' },
  'trust.nodes': { zh: '履约节点', en: 'Fulfillment Nodes' },
  'trust.cats': { zh: '在售品类', en: 'Categories' },
  'trust.advisor': { zh: '贸易顾问', en: 'Trade Advisor' },
  'home.categories': { zh: '品类直达', en: 'Shop by Category' },
  'home.allCats': { zh: '全部', en: 'All' },
  'home.viewMore': { zh: '查看更多', en: 'View More' },
  'home.inquire': { zh: '询价', en: 'Inquire' },
  'home.unitTon': { zh: '/ 吨', en: '/t' },
  'home.unitKg': { zh: '/公斤', en: '/kg' },
  'home.priceOnRequest': { zh: '价格待询', en: 'Price on request' },
  'common.loading': { zh: '加载中…', en: 'Loading…' },
  'product.notFound': { zh: '商品不存在', en: 'Product not found' },
  'product.pickSpec': { zh: '选择规格', en: 'Select spec' },
  'product.pickSpecCount': { zh: '选择规格（共 {n} 种）', en: 'Select spec ({n} variants)' },
  'product.spec': { zh: '规格', en: 'Spec' },
  'product.noSku': { zh: '暂无可售规格', en: 'No specs available' },
  'product.perTonFrom': { zh: '/ 吨起', en: '/t from' },
  'product.inquireQuote': { zh: '询盘获取报价', en: 'Inquire for quote' },
  'product.buyNow': { zh: '立即购买', en: 'Buy now' },
  'product.loginPromptTitle': { zh: '登录后可询盘或下单', en: 'Log in to inquire or order' },
  'product.loginPromptDesc': { zh: '登录买家账号即可发起询盘获取正式报价，或直接下单预订该商品。', en: 'Log in to your buyer account to request a formal quote or place an order directly.' },
  'inquiry.containerEst': { zh: '约需 {n} 个整柜（每柜 {q} 吨）', en: 'About {n} containers ({q} t each)' },
  'inquiry.byTon': { zh: '按吨', en: 'By ton' },
  'inquiry.byCarton': { zh: '按箱', en: 'By carton' },
  'inquiry.quantityCarton': { zh: '数量（箱）', en: 'Quantity (cartons)' },
  'inquiry.quantityTonHint': { zh: '货物净重，不含包装及托盘', en: 'Net weight of goods, excl. packaging & pallet' },
  'inquiry.quantityCartonHint': { zh: '按箱填报，需填「外包装每箱净重」换算吨数', en: 'Carton entry; tons derived from net weight per carton' },
  'inquiry.cartonNeedWeight': { zh: '请先填写「外包装每箱净重」', en: 'Please fill net weight per carton first' },
  'inquiry.cartonEst': { zh: '≈ {n} 箱{rest}', en: '≈ {n} cartons{rest}' },
  'inquiry.cartonRemainder': { zh: '（余 {kg}kg）', en: ' ({kg} kg remainder)' },
  'inquiry.tonEst': { zh: '≈ {t} 吨（净重）', en: '≈ {t} tons (net)' },
  'product.priceLabel': { zh: '行情参考价', en: 'Market Reference' },
  'product.priceTBD': { zh: '价格待询，请联系贸易顾问', en: 'Price on request' },
  'home.specCount': { zh: '种规格可选', en: 'spec options' },
  'service.title': { zh: '服务与保障', en: 'Service & Assurance' },
  'service.s1t': { zh: '产地直采', en: 'Direct Sourcing' },
  'service.s1d': { zh: '工厂源头直供，去中间环节', en: 'From factories, no middlemen' },
  'service.s2t': { zh: '冷链可视', en: 'Cold-Chain Visible' },
  'service.s2d': { zh: '全程温湿度实时回传', en: 'Real-time temperature tracking' },
  'service.s3t': { zh: '单证合规', en: 'Compliant Docs' },
  'service.s3d': { zh: '植检、产地证一次办齐', en: 'Phyto & CO handled for you' },
  'service.s4t': { zh: '履约保障', en: 'Fulfillment Assurance' },
  'service.s4d': { zh: '异常理赔进度全程可跟踪', en: 'Track claims end to end' },
  'footer.slogan': { zh: '农产品跨境贸易的全程伙伴，从产地到您手中，每一步都有保障。', en: 'Your end-to-end partner for cross-border agricultural trade. From origin to your door, with guarantee at every step.' },
  'footer.contact': { zh: '联系我们', en: 'Contact Us' },

  // === 全站 EN 静态文案翻译自动合并 (2026-10-14) ===
  'auth.browseAround': { zh: '随便看看', en: 'Just Browsing' },
  'auth.goLogin': { zh: '去登录', en: 'Sign In' },
  'auth.promptDesc': { zh: '登录后即可发起询盘、下单与管理订单，体验完整的全球农产品履约服务。', en: 'Sign in to request quotes, place orders, and manage them with our full cross-border fulfillment service.' },
  'auth.promptTitle': { zh: '请先登录后查看', en: 'Please sign in to continue' },
  'claim.amount': { zh: '申请金额', en: 'Claim amount' },
  'claim.applyPayout': { zh: '申请赔付', en: 'Claim amount' },
  'claim.back': { zh: '‹ 返回', en: '‹ Back' },
  'claim.backToCenter': { zh: '返回理赔中心', en: 'Back to Claims Center' },
  'claim.cancel': { zh: '取消', en: 'Cancel' },
  'claim.center': { zh: '理赔中心', en: 'Claims Center' },
  'claim.deductible': { zh: '免赔', en: 'Deductible' },
  'claim.desc': { zh: '理赔描述（不少于 20 字）', en: 'Claim description (min. 20 chars)' },
  'claim.detail': { zh: '理赔详情', en: 'Claim Details' },
  'claim.dialogTitle': { zh: '发起异议 / 理赔', en: 'Raise Dispute / Claim' },
  'claim.durationFor': { zh: '持续', en: 'for' },
  'claim.evidenceChain': { zh: '证据链（IoT 数据定责）', en: 'Evidence Chain (IoT-based liability)' },
  'claim.history': { zh: '历史理赔', en: 'Claim history' },
  'claim.liabilitySplit': { zh: '责任划分', en: 'Liability Split' },
  'claim.noEvidence': { zh: '暂无证据数据', en: 'No evidence data' },
  'claim.noLiability': { zh: '暂无定责数据', en: 'No liability data' },
  'claim.noLogs': { zh: '暂无流转记录', en: 'No status logs' },
  'claim.noOrder': { zh: '暂无订单', en: 'No orders' },
  'claim.notFound': { zh: '理赔单不存在', en: 'Claim not found' },
  'claim.order': { zh: '订单', en: 'Order' },
  'claim.payoutRatio': { zh: '赔付比例', en: 'Payout ratio' },
  'claim.progress': { zh: '处理进度', en: 'Progress' },
  'claim.promise1': { zh: '48h 响应', en: '48h response' },
  'claim.promise2': { zh: '14 天赔付', en: '14-day payout' },
  'claim.promise3': { zh: '数据定责', en: 'Data-based liability' },
  'claim.promiseTitle': { zh: '🛡 BluGuard 理赔承诺', en: '🛡 BluGuard Claim Promise' },
  'claim.raiseBtn': { zh: '一键发起异议 / 理赔', en: 'Raise a dispute / claim' },
  'claim.relateOrder': { zh: '关联订单', en: 'Related order' },
  'claim.ruleSnapshot': { zh: '命中规则快照', en: 'Rule Snapshot' },
  'claim.statActive': { zh: '处理中', en: 'In progress' },
  'claim.statClosed': { zh: '已结案', en: 'Closed' },
  'claim.statPaid': { zh: '已获赔', en: 'Paid out' },
  'claim.submit': { zh: '提交', en: 'Submit' },
  'claim.subtitle': { zh: 'BluGuard 智能理赔', en: 'BluGuard Smart Claims' },
  'claim.tempThreshold': { zh: '超温阈值', en: 'Over-temp threshold' },
  'claim.type': { zh: '理赔类型', en: 'Claim type' },
  'claim.viewDetail': { zh: '查看详情 ›', en: 'View details ›' },
  'claim.whatsapp': { zh: '💬 WhatsApp 联系专属客服', en: '💬 WhatsApp support' },
  'comp.chatCustomer': { zh: '我', en: 'Me' },
  'comp.chatStaff': { zh: '客', en: 'CS' },
  'comp.creditAvailable': { zh: '可用额度', en: 'Available' },
  'comp.creditInUse': { zh: '在途占用', en: 'In Use' },
  'comp.creditNextDue': { zh: '下次到期', en: 'Next Due' },
  'comp.creditRating': { zh: '信用评级', en: 'Credit Rating' },
  'comp.creditRatingBadge': { zh: '评级', en: 'Rating' },
  'comp.creditTitle': { zh: 'BluCredit 信用账期', en: 'BluCredit Credit Terms' },
  'comp.creditTotal': { zh: '总额度', en: 'Total Limit' },
  'comp.lanbaoFabLabel': { zh: '蓝宝 AI', en: 'LanBao AI' },
  'doc.attachment': { zh: '附件', en: 'Attachment' },
  'doc.carbonReduction': { zh: '减碳', en: 'Carbon reduction' },
  'doc.catAll': { zh: '全部', en: 'All' },
  'doc.catArrival': { zh: '到港', en: 'Arrival' },
  'doc.catCertPack': { zh: '四证包', en: 'Cert Pack' },
  'doc.catOther': { zh: '其他', en: 'Other' },
  'doc.catTempControl': { zh: '温控', en: 'Temp Control' },
  'doc.compliant': { zh: '达标', en: 'Compliant' },
  'doc.days': { zh: '天', en: 'days' },
  'doc.download': { zh: '下载', en: 'Download' },
  'doc.receiverPlan': { zh: '收款计划', en: 'Payment Plan' },
  'doc.remaining': { zh: '剩余', en: 'Remaining' },
  'doc.shaHash': { zh: 'SHA-256 哈希验真码', en: 'SHA-256 Verification Hash' },
  'doc.slipNo': { zh: '水单号', en: 'Slip No.' },
  'doc.uploadSlip': { zh: '上传水单', en: 'Upload Slip' },
  'doc.uploaded': { zh: '已上传', en: 'Uploaded' },
  'footer.brandMark': { zh: '蓝', en: 'B' },
  'fulfill.bluGuardPlan': { zh: 'BluGuard 预案已启动', en: 'BluGuard Plan Activated' },
  'fulfill.brand': { zh: '蓝星履约', en: 'BluPlanet' },
  'fulfill.contact': { zh: 'WhatsApp 联系蓝星团队', en: 'WhatsApp BluPlanet Team' },
  'fulfill.containerNo': { zh: '柜号', en: 'Container No.' },
  'fulfill.currentLocation': { zh: '实时位置', en: 'Current Location' },
  'fulfill.currentStage': { zh: '当前阶段', en: 'Current Stage' },
  'fulfill.currentTemp': { zh: '当前温度', en: 'Current Temp' },
  'fulfill.day': { zh: '天', en: 'd' },
  'fulfill.daysAgo': { zh: '天前', en: 'd ago' },
  'fulfill.demo': { zh: '演示数据', en: 'Demo data' },
  'fulfill.departurePort': { zh: '起运港', en: 'Departure Port' },
  'fulfill.destinationPort': { zh: '目的港', en: 'Destination Port' },
  'fulfill.disclaimer': { zh: '此链接由蓝宝平台生成 · 仅供查看订单履约进度 · 请勿转发给无关人员', en: 'Generated by BluPlanet · For order tracking only · Do not share' },
  'fulfill.documents': { zh: '单证附件', en: 'Documents' },
  'fulfill.eta': { zh: '预计到港', en: 'ETA' },
  'fulfill.freq': { zh: '10 分钟/条 · 存储转发', en: 'Every 10 min · store & forward' },
  'fulfill.hour': { zh: '小时', en: 'h' },
  'fulfill.hoursAgo': { zh: '小时前', en: 'h ago' },
  'fulfill.inTemp': { zh: '箱内实时温度', en: 'In-container Temp' },
  'fulfill.iotCurve': { zh: 'IoT 温控曲线', en: 'IoT Temperature Curve' },
  'fulfill.justNow': { zh: '刚刚', en: 'just now' },
  'fulfill.loadedAfter': { zh: '装柜后', en: 'Loaded +' },
  'fulfill.locating': { zh: '定位中', en: 'Locating' },
  'fulfill.min': { zh: '分钟', en: 'min' },
  'fulfill.minutesAgo': { zh: '分钟前', en: 'm ago' },
  'fulfill.noDocuments': { zh: '暂无附件', en: 'No documents' },
  'fulfill.noDownloadPerm': { zh: '无下载权限，请联系管理员', en: 'No permission. Please contact admin.' },
  'fulfill.noRt': { zh: '无实时数据', en: 'No live data' },
  'fulfill.noTracking': { zh: '暂无履约进度记录', en: 'No tracking records yet' },
  'fulfill.nodePhoto': { zh: '节点影像', en: 'Photo' },
  'fulfill.nodeProgress': { zh: '10节点进度', en: '10-Node Progress' },
  'fulfill.nodeVideo': { zh: '节点视频', en: 'Video' },
  'fulfill.now': { zh: '当前', en: 'Now' },
  'fulfill.orderNo': { zh: '订单号', en: 'Order No.' },
  'fulfill.orderNotFound': { zh: '未找到订单信息', en: 'Order not found' },
  'fulfill.overLimit': { zh: '超限', en: 'Over-limit' },
  'fulfill.overLimitDuration': { zh: '超限时长', en: 'Over-limit Duration' },
  'fulfill.progress': { zh: '进度', en: 'Progress' },
  'fulfill.ribbon': { zh: '冷链带 · 全航程温控状态', en: 'Cold-chain band · full-voyage status' },
  'fulfill.ribbonBreach': { zh: '超限', en: 'Breach' },
  'fulfill.ribbonOk': { zh: '达标', en: 'In range' },
  'fulfill.noStandard': { zh: '未设置温控标准', en: 'No standard set' },
  'fulfill.noStandardStats': { zh: '商品未设置温控标准，暂不统计达标率', en: 'No temperature standard set for this product; compliance stats unavailable' },
  'fulfill.shippingCompany': { zh: '船公司', en: 'Carrier' },
  'fulfill.target': { zh: '目标', en: 'Target' },
  'fulfill.targetBand': { zh: '目标区间', en: 'Target Band' },
  'fulfill.targetTemp': { zh: '目标温度', en: 'Target Temp' },
  'fulfill.tempLabel': { zh: '箱温', en: 'Temp' },
  'fulfill.tempOk': { zh: '正常', en: 'Normal' },
  'fulfill.tempOver': { zh: '温度超限', en: 'Temp Over-limit' },
  'fulfill.tracking': { zh: '履约进度追踪', en: 'Fulfillment Tracking' },
  'fulfill.updated': { zh: '更新于', en: 'Updated' },
  'fulfill.uploadedAt': { zh: '上传于', en: 'Uploaded' },
  'fulfill.upperLimit': { zh: '上限', en: 'Upper Limit' },
  'fulfill.alarmLow': { zh: '报警下限', en: 'Lower Limit' },
  'fulfill.alarmHigh': { zh: '报警上限', en: 'Upper Limit' },
  'fulfill.alarmRange': { zh: '报警范围', en: 'Alarm Range' },
  'fulfill.tempBandOk': { zh: '达标', en: 'In range' },
  'fulfill.tempDeviate': { zh: '偏离', en: 'Deviation' },
  'fulfill.tempAlarm': { zh: '报警', en: 'Alarm' },
  'fulfill.ribbonDeviate': { zh: '偏离', en: 'Deviation' },
  'fulfill.ribbonAlarm': { zh: '报警', en: 'Alarm' },
  'fulfill.vessel': { zh: '船名', en: 'Vessel' },
  'inquiry.catSwitchHint': { zh: '（点表单区品类可切换）', en: ' (tap a category below to switch)' },
  'inquiry.currentCat': { zh: '当前品类：', en: 'Current category:' },
  'inquiry.destLabel': { zh: '目的地：', en: 'Destination: ' },
  'inquiry.freeDesc': { zh: '自由描述', en: 'Free description' },
  'inquiry.listEmpty': { zh: '暂无询盘，去首页挑选农产品发起询盘吧。', en: 'No inquiries yet. Browse the homepage to start one.' },
  'inquiry.listLoginHint': { zh: '请先登录买家账号查看询盘。', en: 'Please sign in to view your inquiries.' },
  'inquiry.moreItemsPrefix': { zh: ' 等 ', en: ' + ' },
  'inquiry.moreItemsSuffix': { zh: ' 项', en: ' items' },
  'inquiry.moreItemsTotalPrefix': { zh: '…等共 ', en: '... ' },
  'inquiry.packCalciumBox': { zh: '钙塑箱', en: 'Calcium-plastic box' },
  'inquiry.packCarton': { zh: '纸箱', en: 'Carton' },
  'inquiry.packFoamBox': { zh: '泡沫箱', en: 'Foam box' },
  'inquiry.packNetBag': { zh: '网袋', en: 'Net bag' },
  'inquiry.packNone': { zh: '无', en: 'None' },
  'inquiry.packPlasticBasket': { zh: '塑料筐', en: 'Plastic basket' },
  'inquiry.packVacuumBag': { zh: '真空袋', en: 'Vacuum bag' },
  'inquiry.productFallback': { zh: '商品', en: 'Product' },
  'inquiry.quoteNo': { zh: '单号', en: 'No.' },
  'inquiry.quoteReply': { zh: '报价回复：', en: 'Quote reply: ' },
  'inquiry.refPriceLabel': { zh: '参考价', en: 'Reference price' },
  'inquiry.statusOrdered': { zh: '已转订单', en: 'Ordered' },
  'inquiry.statusPending': { zh: '待回复', en: 'Pending' },
  'inquiry.statusProcessing': { zh: '处理中', en: 'Processing' },
  'inquiry.createNew': { zh: '发起新询盘', en: 'New Inquiry' },
  'inquiry.statusQuoted': { zh: '已报价', en: 'Quoted' },
  'inquiry.submitFailed': { zh: '提交失败，请稍后重试', en: 'Submission failed, please try again' },
  'inquiry.targetTermLabel': { zh: '贸易术语：', en: 'Terms: ' },
  'inquiry.unitPrice': { zh: '单价', en: 'Unit price' },
  'inquiry.unitTon': { zh: '吨', en: 't' },
  'inquiry.viewQuoteOrder': { zh: '查看报价并下单', en: 'View quote & order' },
  'message.empty': { zh: '暂无消息，报价回复会在这里通知你', en: 'No messages yet — quote replies will appear here' },
  'message.loadFailed': { zh: '加载失败', en: 'Failed to load' },
  'message.loginToView': { zh: '请先登录后查看消息', en: 'Please sign in to view messages' },
  'message.notification': { zh: '通知', en: 'Notification' },
  'message.titleInquirySubmitted': { zh: '询盘已提交', en: 'Inquiry Submitted' },
  'message.titleInquiryReply': { zh: '您的询盘已收到报价回复', en: 'Your Inquiry Received a Quotation' },
  'message.titleQuoteAccepted': { zh: '报价单已确认', en: 'Quotation Confirmed' },
  'message.titleOrderCreated': { zh: '报价单已转订单', en: 'Quotation Converted to Order' },
  'message.typeInquiryReply': { zh: '报价回复', en: 'Quote Reply' },
  'message.typeInquiryStatus': { zh: '询盘状态', en: 'Inquiry Status' },
  'message.typeSystem': { zh: '系统通知', en: 'System Notice' },
  'message.typeOrder': { zh: '订单通知', en: 'Order Update' },
  'order.about': { zh: '约', en: 'about' },
  'order.buyerCenterOverview': { zh: '买家中心 · 账户概览', en: 'Buyer Center · Account Overview' },
  'order.confirmOrder': { zh: '确认下单', en: 'Confirm order' },
  'order.containers': { zh: '柜', en: 'containers' },
  'order.createFailed': { zh: '下单失败，请稍后重试', en: 'Order failed, please retry' },
  'order.createSuccess': { zh: '下单成功', en: 'Order placed' },
  'order.creditRating': { zh: '信用评级', en: 'Credit rating' },
  'order.due': { zh: '到期', en: 'Due' },
  'order.fullContainerUnit': { zh: '个整柜', en: 'full containers' },
  'order.historyLabel': { zh: '历史订单', en: 'Order history' },
  'order.inTransitAmount': { zh: '在途金额', en: 'In-transit amount' },
  'order.inTransitLabel': { zh: '在途订单', en: 'In-transit orders' },
  'order.noDocuments': { zh: '暂无单证', en: 'No documents' },
  'order.notFound': { zh: '未找到该订单', en: 'Order not found' },
  'order.note': { zh: '备注', en: 'Note' },
  'order.paidAmount': { zh: '实付金额', en: 'Amount paid' },
  'order.payPendingVerify': { zh: '待核验', en: 'Pending verification' },
  'order.payRejected': { zh: '已驳回', en: 'Rejected' },
  'order.payToUpload': { zh: '待上传', en: 'Awaiting upload' },
  'order.payUnpaid': { zh: '待付款', en: 'Unpaid' },
  'order.payVerified': { zh: '已核验', en: 'Verified' },
  'order.paymentMethod': { zh: '付款方式', en: 'Payment method' },
  'order.paymentVoucher': { zh: '付款凭证', en: 'Payment Voucher' },
  'order.perContainer': { zh: '每柜', en: 'each' },
  'order.qtyGtZero': { zh: '数量必须大于 0（单位：吨）', en: 'Quantity must be greater than 0 (unit: t)' },
  'order.quickEstimate': { zh: '快速预估（按商品储运属性）', en: 'Quick estimate (by product shipping attributes)' },
  'order.statusCancelled': { zh: '已取消', en: 'Cancelled' },
  'order.statusCompleted': { zh: '已完成', en: 'Completed' },
  'order.statusConfirmed': { zh: '备货中', en: 'Preparing' },
  'order.statusDelivered': { zh: '已送达', en: 'Delivered' },
  'order.statusExportCustoms': { zh: '出口报关', en: 'Export customs' },
  'order.statusImportCustoms': { zh: '进口清关', en: 'Import clearance' },
  'order.statusPending': { zh: '待确认', en: 'Pending confirmation' },
  'order.statusShipping': { zh: '海运中', en: 'In transit (sea)' },
  'order.subDocuments': { zh: '单证', en: 'Documents' },
  'order.subPayment': { zh: '付款', en: 'Payment' },
  'order.subTracking': { zh: '追踪', en: 'Tracking' },
  'order.submitFailed': { zh: '提交失败，请稍后重试', en: 'Submission failed, please retry' },
  'order.tempAlert': { zh: '温度报警', en: 'Temperature alert' },
  'order.tons': { zh: '吨', en: 't' },
  'order.unitPriceLabel': { zh: '单价', en: 'Unit price' },
  'order.viewInOrders': { zh: '在订单中查看', en: 'View in orders' },
  'order.voucherSubmittedHint': { zh: '凭证文件已提交，后台核验中', en: 'Voucher submitted, verification in progress' },
  'order.voucherSubmittedOk': { zh: '付款凭证已提交，等待后台核验', en: 'Payment voucher submitted, awaiting verification' },
  'profile.accountSettings': { zh: '账户与设置', en: 'Account & Settings' },
  'profile.avatarFallback': { zh: '买', en: 'B' },
  'profile.buyerCode': { zh: '买家编码', en: 'Buyer Code' },
  'profile.creditRating': { zh: '信用评级', en: 'Credit Rating' },
  'profile.guestName': { zh: '买家用户', en: 'Buyer User' },
  'profile.joinDate': { zh: '加入时间', en: 'Join Date' },
  'quote.buyer': { zh: '买家', en: 'Buyer' },
  'quote.confirmFailed': { zh: '确认下单失败，请重试', en: 'Failed to confirm order, please retry' },
  'quote.contactCountry': { zh: '联系人 / 国家', en: 'Contact / Country' },
  'quote.convertedHint': { zh: '本报价单已转为订单，可在「我的订单」中查看履约进度。', en: 'This quotation has been converted to an order. Track fulfillment under My Orders.' },
  'quote.costBreakdown': { zh: '成本明细', en: 'Cost breakdown' },
  'quote.createdAt': { zh: '创建时间', en: 'Created' },
  'quote.customsClearanceFee': { zh: '清关费', en: 'Customs clearance' },
  'quote.docFee': { zh: '单证费', en: 'Document fee' },
  'quote.domesticTransport': { zh: '国内运输费', en: 'Domestic transport' },
  'quote.inlandDeliveryFee': { zh: '目的国派送费', en: 'Inland delivery (destination)' },
  'quote.insuranceFee': { zh: '保险费', en: 'Insurance' },
  'quote.invalid': { zh: '报价单无效', en: 'Invalid quotation' },
  'quote.itemDetails': { zh: '商品明细', en: 'Item details' },
  'quote.lines': { zh: '行', en: 'lines' },
  'quote.loadFailed': { zh: '加载失败', en: 'Failed to load' },
  'quote.oceanFreight': { zh: '海运费', en: 'Ocean freight' },
  'quote.pendingPrice': { zh: '待定价', en: 'To be priced' },
  'quote.placing': { zh: '下单中…', en: 'Placing order…' },
  'quote.portMiscFee': { zh: '港杂费', en: 'Port handling fee' },
  'quote.product': { zh: '商品', en: 'Product' },
  'quote.qtyUnit': { zh: '数量 / 单位', en: 'Quantity / Unit' },
  'quote.reviewHint': { zh: '请核对报价明细，确认无误后下单', en: 'Please review the quote details and confirm to place the order' },
  'quote.route': { zh: '航线', en: 'Route' },
  'quote.deliveryPlace': { zh: '交货地', en: 'Delivery place' },
  'quote.settleCurrency': { zh: '结算币种', en: 'Settlement currency' },
  'quote.statusAccepted': { zh: '已接受', en: 'Accepted' },
  'quote.statusConverted': { zh: '已转订单', en: 'Converted to order' },
  'quote.statusDraft': { zh: '草稿', en: 'Draft' },
  'quote.statusExpired': { zh: '已过期', en: 'Expired' },
  'quote.statusRejected': { zh: '已拒绝', en: 'Rejected' },
  'quote.statusSent': { zh: '待接受', en: 'Awaiting acceptance' },
  'quote.taxes': { zh: '税费', en: 'Taxes' },
  'quote.title': { zh: '报价单', en: 'Quotation' },
  'quote.totalAmount': { zh: '总金额', en: 'Total amount' },
  'quote.unitPrice': { zh: '单价', en: 'Unit price' },
  'quote.perCarton': { zh: '≈ {v}/箱', en: '≈ {v}/carton' },
  'quote.cartonQty': { zh: '≈ {n} 箱', en: '≈ {n} cartons' },
  'quote.validDays': { zh: '有效期(天)', en: 'Validity (days)' },
  'quote.validUntil': { zh: '报价截止日', en: 'Quote expiry' },
  'share.buyer': { zh: '买家', en: 'Buyer' },
  'share.contactCountry': { zh: '联系人 / 国家', en: 'Contact / Country' },
  'share.contactSales': { zh: '请联系您的销售人员获取新的分享链接', en: 'Please contact your sales representative for a new link' },
  'share.costCustoms': { zh: '清关费', en: 'Customs Clearance' },
  'share.costDetail': { zh: '成本明细', en: 'Cost Breakdown' },
  'share.costDocFee': { zh: '单证费', en: 'Documentation Fee' },
  'share.costDomesticTransport': { zh: '国内运输费', en: 'Domestic Transport' },
  'share.costInlandDelivery': { zh: '目的国派送费', en: 'Inland Delivery' },
  'share.costInsurance': { zh: '保险费', en: 'Insurance' },
  'share.costOceanFreight': { zh: '海运费', en: 'Ocean Freight' },
  'share.costPortCharges': { zh: '港杂费', en: 'Port Charges' },
  'share.costTaxes': { zh: '税费', en: 'Taxes & Duties' },
  'share.createdAt': { zh: '创建时间', en: 'Created' },
  'share.currency': { zh: '结算币种', en: 'Currency' },
  'share.enter': { zh: '进入 Bluplanet 买家门户', en: 'Enter Bluplanet Buyer Portal' },
  'share.invalidLink': { zh: '无效的分享链接', en: 'Invalid share link' },
  'share.linkExpired': { zh: '链接已失效', en: 'Link Expired' },
  'share.loadFailed': { zh: '获取订单信息失败', en: 'Failed to load order info' },
  'share.networkError': { zh: '网络错误，请稍后重试', en: 'Network error, please retry later' },
  'share.noDocDownload': { zh: '当前分享链接未开放单证下载，请联系管理员', en: 'Document download is not allowed for this link. Please contact admin.' },
  'share.product': { zh: '商品', en: 'Product' },
  'share.qtyUnit': { zh: '数量 / 单位', en: 'Quantity / Unit' },
  'share.quotationInvalid': { zh: '分享链接无效', en: 'Invalid share link' },
  'share.quotationLoadFailed': { zh: '加载失败', en: 'Failed to load' },
  'share.quotationSub': { zh: '您正通过分享链接查看此报价单（免登录）', en: 'You are viewing this quotation via a share link (no login required)' },
  'share.quotationTitle': { zh: '报价单', en: 'Quotation' },
  'share.route': { zh: '航线', en: 'Route' },
  'share.deliveryPlace': { zh: '交货地', en: 'Delivery place' },
  'share.serverError': { zh: '服务器错误', en: 'Server error' },
  'share.spec': { zh: '规格', en: 'Specification' },
  'share.total': { zh: '总金额', en: 'Total Amount' },
  'share.tradeTerm': { zh: '贸易术语', en: 'Trade Term' },
  'share.unitPrice': { zh: '单价', en: 'Unit Price' },
  'share.validDays': { zh: '有效期(天)', en: 'Validity (days)' },
  'share.validUntil': { zh: '报价截止日', en: 'Quote Deadline' },
  'voucher.chainVerified': { zh: '✓ 所有单证 SHA-256 已上链', en: '✓ All documents SHA-256 anchored on-chain' },
  'voucher.fileTooLarge': { zh: '文件大小不能超过 10MB', en: 'File size must be within 10MB' },
  'voucher.paidAmount': { zh: '实付金额', en: 'Amount Paid' },
  'voucher.payerNote': { zh: '付款备注（可选）', en: 'Payment Note (optional)' },
  'voucher.pdfSelected': { zh: 'PDF 凭证已选择，提交后可由后台查看。', en: 'PDF voucher selected; staff can view it after submission.' },
  'voucher.previewAlt': { zh: '付款凭证预览', en: 'Payment voucher preview' },
  'voucher.reselect': { zh: '重新选择', en: 'Reselect' },
  'voucher.scanVerify': { zh: '扫码验真', en: 'Scan to Verify' },
  'voucher.selectFile': { zh: '选择凭证图片/PDF', en: 'Select voucher image / PDF' },
  'voucher.selectFirst': { zh: '请先选择付款凭证', en: 'Please select a payment voucher first' },
  'voucher.selected': { zh: '已选择：', en: 'Selected: ' },
  'voucher.submit': { zh: '提交付款凭证', en: 'Submit Payment Voucher' },
  'voucher.submitting': { zh: '提交中...', en: 'Submitting…' },
  'voucher.uploadTitle': { zh: '上传付款凭证', en: 'Upload Payment Voucher' },
  'voucher.verifyCardDesc': { zh: '扫码核验单证真伪', en: 'Scan to verify document authenticity' },
  'voucher.verifyCardTitle': { zh: '哈希验真卡', en: 'Hash Verification Card' },

  // ===== 以下由页面内联中英文判断迁入（统一 i18n）=====
  // ---- api ----
  'api.badResponse': { zh: '服务器响应异常 ({status})', en: 'Unexpected server response ({status})' },
  'api.requestFailed': { zh: '请求失败 ({code})', en: 'Request failed ({code})' },
  'api.sessionExpired': { zh: '登录已失效，请重新登录', en: 'Your session has expired. Please sign in again.' },
  // ---- auth ----
  'auth.emailRequired': { zh: '请输入邮箱', en: 'Please enter your email' },
  // ---- chat ----
  'chat.AI': { zh: 'AI 接待', en: 'AI assistant' },
  'chat.CLOSED': { zh: '会话已结束', en: 'Conversation closed' },
  'chat.HUMAN': { zh: '人工客服接待中', en: 'Human support' },
  'chat.WAITING': { zh: '等待人工接待，可继续留言', en: 'Waiting for a person. You can leave a message.' },
  'chat.error': { zh: '连接失败，请稍后重试；未发送的内容已保留。', en: 'Connection failed. Please retry; your draft is saved.' },
  'chat.expired': { zh: '会话已过期，请开始新会话。', en: 'Session expired. Start a new conversation.' },
  'chat.human': { zh: '转人工', en: 'Talk to a person' },
  'chat.placeholder': { zh: '问问物流、报价、贸易术语…', en: 'Ask about shipping, quotes, trade terms…' },
  'chat.restart': { zh: '新会话', en: 'New conversation' },
  'chat.send': { zh: '发送', en: 'Send' },
  'chat.staff': { zh: '人工客服', en: 'Support agent' },
  'chat.thinking': { zh: 'AI 正在回复…', en: 'AI is replying…' },
  'chat.welcome': { zh: '您好，我是 Bluplanet 贸易助手。可以问我物流追踪、如何询盘报价、贸易术语区别等～', en: 'Hi! I\'m the Bluplanet trade assistant. Ask me about shipment tracking, how to request a quote, or trade terms.' },
  // ---- comp ----
  'comp.clickToChatDragTo': { zh: '点击咨询 · 拖动调整位置', en: 'Click to chat · Drag to move' },
  // ---- hero ----
  'hero.getAQuote': { zh: '开始询价', en: 'Get a quote' },
  'hero.tellUsWhatYouNeed': { zh: '请输入采购需求', en: 'Tell us what you need…' },
  // ---- home ----
  'home.loadMore': { zh: '加载更多', en: 'Load more' },
  'home.retry': { zh: '重试', en: 'Retry' },
  'home.showAllCategories': { zh: '展开全部品类（{count}）', en: 'Show all categories ({count})' },
  'home.showFewerCategories': { zh: '收起品类', en: 'Show fewer categories' },
  'home.unableToLoadProductsPlease': { zh: '商品加载失败，请重试。', en: 'Unable to load products. Please retry.' },
  // ---- inquiry ----
  'inquiry.compareQuotes': { zh: '比较报价方案', en: 'Compare quotes' },
  'inquiry.sentquotescountQuoteOptions': { zh: '{sentQuotesCount} 份方案可选 · ', en: '{sentQuotesCount} quote options · ' },
  'inquiry.viewConversationProgress': { zh: '查看沟通记录与进度', en: 'View conversation & progress' },
  // ---- inquiryText ----
  'inquiryText.bulkPackedNoInnerBags': { zh: '箱内不再分装（散装）', en: 'Bulk packed (no inner bags)' },
  'inquiryText.compareAvailableRoutes': { zh: '请平台比较可用航线', en: 'Compare available routes' },
  'inquiryText.notSureYet': { zh: '暂不确定', en: 'Not sure yet' },
  'inquiryText.smallerBagsInsideTheCarton': { zh: '箱内分装小袋', en: 'Smaller bags inside the carton' },
  // ---- journey ----
  'journey.aMixedOrderNeedsA': { zh: '同单混装需要先确认每箱净重，才能准确拆分各包装的数量。请先补充箱规。', en: 'A mixed order needs a confirmed net weight per carton so the quantities can be split accurately.' },
  'journey.aSimpleWayToRequest': { zh: '说一句需求，开始询价', en: 'A simple way to request a quote' },
  'journey.aboutSailingdaysSailingDays': { zh: '参考航行 {sailingDays} 天', en: 'About {sailingDays} sailing days' },
  'journey.addADetailOrAsk': { zh: '补充信息，或问蓝宝一个问题…', en: 'Add a detail or ask a question…' },
  'journey.allocatedAllocationtotalCartonsV': { zh: '已分配 {allocationTotal} 箱{total}', en: 'Allocated: {allocationTotal} cartons{total}' },
  'journey.allocatedAbout': { zh: ' / 总量约 {n} 箱', en: ' / about {n} total' },
  'journey.attachments': { zh: '附件', en: 'Attachments' },
  'journey.basedOnCurrentRouteData': { zh: '按目的港可用航线估算，最早约 {date} 到货（航程 {sailingDays} 天 + 7 天履约缓冲），当前日期可能无法满足。请调整到该日期或之后。', en: 'Based on current route data, the earliest estimated arrival is {date} ({sailingDays} sailing days + 7 days handling). Please choose this date or later.' },
  'journey.basedOnCurrentRouteData2': { zh: '按目前可用航线估算，最早约 {date} 到货；最终时效会在报价时确认。', en: 'Based on current route data, the earliest estimated arrival is {date}; the final schedule will be confirmed with your quote.' },
  'journey.calciumPlasticBox': { zh: '钙塑箱', en: 'Calcium plastic box' },
  'journey.cartonStandardExport': { zh: '纸箱', en: 'Carton (standard export)' },
  'journey.cartonWeightExceedsTheSelected': { zh: '每箱净重不符合所选 SKU 的规格范围，请调整箱重或重新选择 SKU。', en: 'Carton weight exceeds the selected SKU specification. Please revise it.' },
  'journey.cartonsPerPackageConfiguration': { zh: '请填写每种包装需要的箱数', en: 'Cartons per package configuration' },
  'journey.cfr': { zh: 'CFR · 成本加运费', en: 'CFR' },
  'journey.changeDate': { zh: '修改到货日期', en: 'Change date' },
  'journey.chooseAQuoteQuoteoptionscount': { zh: '选择报价方案（{quoteOptionsCount}）', en: 'Choose a quote ({quoteOptionsCount})' },
  'journey.chooseArrivalDate': { zh: '选择期望到货日期', en: 'Choose arrival date' },
  'journey.chooseOneOrTypeYour': { zh: '可以直接点选，也可以自己输入：', en: 'Choose one or type your own:' },
  'journey.cif': { zh: 'CIF · 成本、保险费加运费', en: 'CIF' },
  'journey.closeCalendar': { zh: '关闭日历', en: 'Close calendar' },
  'journey.collectedSoFar': { zh: '已整理的信息', en: 'Collected so far' },
  'journey.compareAvailableTerms': { zh: '不确定，请给我多种方案', en: 'Compare available terms' },
  'journey.compareQuoteOptions': { zh: '查看并比较报价方案', en: 'Compare quote options' },
  'journey.compareTerms': { zh: '请提供多种方案', en: 'Compare terms' },
  'journey.compareWithClarity': { zh: '报价看得明白', en: 'Compare with clarity' },
  'journey.confirmSplit': { zh: '确认包装分配', en: 'Confirm split' },
  'journey.countMeshBagsV': { zh: '{count} 件网袋 × {v}', en: '{count} mesh bags × {v}' },
  'journey.customPackaging': { zh: '定制包装：', en: 'Custom packaging: ' },
  'journey.customPackagingDetailsToFollow': { zh: '定制包装要求稍后补充', en: 'Custom packaging details to follow' },
  'journey.describeTheNumberAndNet': { zh: '请描述每箱内袋的数量和每袋净重。', en: 'Describe the number and net weight of the inner bags per carton.' },
  'journey.describeTheOuterPackagingYou': { zh: '请描述您希望使用的外包装类型。', en: 'Describe the outer packaging you have in mind.' },
  'journey.editAnyDetailBeforeSending': { zh: '提交前可以直接修改；交期、贸易条款和包装暂时不确定也不影响提交。', en: 'Edit any detail before sending. Delivery timing, trade terms and packaging are optional.' },
  'journey.enterKgPerCarton': { zh: '请输入每箱净重…', en: 'Enter kg per carton…' },
  'journey.enterTheNetWeightPer': { zh: '请填写每箱净重，例如 8 公斤/箱。', en: 'Enter the net weight per carton, for example 8 kg per carton.' },
  'journey.fasterDelivery': { zh: '尽量快，优先时效', en: 'Faster delivery' },
  'journey.fewerTransfers': { zh: '尽量少中转', en: 'Fewer transfers' },
  'journey.fileUploadFailedPleaseTry': { zh: '附件上传失败，请重试。', en: 'File upload failed. Please try again.' },
  'journey.foamBoxDirectPack': { zh: '泡沫箱（直接装箱）', en: 'Foam box (direct pack)' },
  'journey.fob': { zh: 'FOB · 装运港交货', en: 'FOB' },
  'journey.goodsTotal': { zh: '商品总价', en: 'Goods total' },
  'journey.inquiryConversation': { zh: '询盘沟通记录', en: 'Inquiry conversation' },
  'journey.inquiryConversation2': { zh: '询价对话', en: 'Inquiry conversation' },
  'journey.inquirySubmitted': { zh: '询价已提交', en: 'Inquiry submitted' },
  'journey.kgKgCarton': { zh: '{kg} 公斤/箱', en: '{kg} kg / carton' },
  'journey.lowerCost': { zh: '费用优先', en: 'Lower cost' },
  'journey.meshBag': { zh: '网袋', en: 'Mesh bag' },
  'journey.n10Bags1Kg': { zh: '10 袋 × 1 公斤', en: '10 bags × 1 kg' },
  'journey.n10Bags1KgEach': { zh: '10 袋 × 1 公斤', en: '10 bags × 1 kg each' },
  'journey.n20Bags500G': { zh: '20 袋 × 500 克', en: '20 bags × 500 g' },
  'journey.n20Bags500GEach': { zh: '20 袋 × 500 克', en: '20 bags × 500 g each' },
  'journey.newInquiry': { zh: '发起新询盘', en: 'New inquiry' },
  'journey.nextMonth': { zh: '下个月', en: 'Next month' },
  'journey.noLongFormsToStart': { zh: '不用一上来填长表', en: 'No long forms to start' },
  'journey.noOtherRequirements': { zh: '无其他特殊要求', en: 'No other requirements' },
  'journey.noPallet': { zh: '不打托', en: 'No pallet' },
  'journey.noPreference': { zh: '暂无偏好，由平台安排', en: 'No preference' },
  'journey.noSkuMatchesTheseRequirements': { zh: '目录中没有匹配的商品规格，请补充或修改品种、规格，也可以选择暂不确定，由平台确认。', en: 'No SKU matches these requirements. Please clarify the variety/specification or ask us to confirm it.' },
  'journey.notSureRecommendForMe': { zh: '暂不确定，请平台推荐', en: 'Not sure; recommend for me' },
  'journey.nothingMovesWithoutYourSay': { zh: '关键决定始终由你做', en: 'Nothing moves without your say' },
  'journey.oneLargeBagBulkPack': { zh: '一个大包装/整袋装', en: 'One large bag / bulk pack' },
  'journey.organizing': { zh: '正在整理…', en: 'Organizing…' },
  'journey.originPort': { zh: '装运港', en: 'Origin port' },
  'journey.otherCartonSize': { zh: '其他箱规', en: 'Other carton size' },
  'journey.otherInnerBagConfiguration': { zh: '其他内袋规格', en: 'Other inner bag configuration' },
  'journey.otherPack': { zh: '其他规格', en: 'Other pack' },
  'journey.otherRequirements': { zh: '其他要求：', en: 'Other requirements: ' },
  'journey.otherRequirementsToFollow': { zh: '其他要求稍后补充', en: 'Other requirements to follow' },
  'journey.palletized': { zh: '打托', en: 'Palletized' },
  'journey.pkg.cartonNet': { zh: '每箱净重：{weight}', en: 'Carton net weight: {weight} per carton' },
  'journey.pkg.inner': { zh: '内包装：{value}', en: 'Inner packaging: {value}' },
  'journey.pkg.outer': { zh: '外包装：{value}', en: 'Outer packaging: {value}' },
  'journey.plasticCrate': { zh: '塑料筐', en: 'Plastic crate' },
  'journey.platformToRecommendSuitableOuter': { zh: '请平台按商品和运输方式推荐', en: 'Platform to recommend suitable outer packaging' },
  'journey.pleaseChooseADateFrom': { zh: '请从日期选择器中选一个日期，或选择“尽快安排 / 时间灵活”。', en: 'Please choose a date from the calendar, or select “As soon as possible” or “Timing is flexible”.' },
  'journey.pleaseCompleteTheCartonSplit': { zh: '请先完成每种内包装对应的箱数分配。', en: 'Please complete the carton split for each packaging configuration.' },
  'journey.pleaseConfirmTheResolvedProduct': { zh: '请先确认重新匹配的商品和规格，再提交询盘。', en: 'Please confirm the resolved product and specification before submitting.' },
  'journey.pleaseDescribeTheCustomRequirements': { zh: '请描述具体要求，例如包装尺寸、Logo/印刷或唛头内容。图稿还没定也没关系，先写明大致要求即可。', en: 'Please describe the custom requirements, such as box dimensions, logo or carton marks. A rough description is fine if the artwork is not ready.' },
  'journey.pleaseEnterACartonWeight': { zh: '请填写所选 SKU 规格范围内的每箱净重。', en: 'Please enter a carton weight within the selected SKU specification.' },
  'journey.pleaseEnterADestination': { zh: '请填写目的地', en: 'Please enter a destination' },
  'journey.pleaseEnterAPositiveCarton': { zh: '请按包装方案顺序，为 {innerPackConfigsCount} 种包装分别填写大于 0 的箱数。', en: 'Please enter a positive carton count for every configuration.' },
  'journey.preferAFormEnterDetails': { zh: '更习惯逐项填写？改用表单录入', en: 'Prefer a form? Enter details one by one' },
  'journey.previousMonth': { zh: '上个月', en: 'Previous month' },
  'journey.productNameOrDescription': { zh: '商品名称或描述', en: 'Product name or description' },
  'journey.quantityMustBeGreaterThan': { zh: '数量必须大于 0', en: 'Quantity must be greater than 0' },
  'journey.quoteDetails': { zh: '报价详情', en: 'Quote details' },
  'journey.quotePreparation': { zh: '准备报价', en: 'Quote preparation' },
  'journey.recommendForMe': { zh: '请平台推荐', en: 'Recommend for me' },
  'journey.requestAQuote': { zh: '询价', en: 'Request a quote' },
  'journey.requestReceived': { zh: '询价已提交', en: 'Request received' },
  'journey.requestedShipDate': { zh: '期望出运', en: 'Requested ship date' },
  'journey.requestedShipDateNotedShipmentdate': { zh: '已记录期望出运日期：{shipmentDate}。', en: 'Requested ship date noted: {shipmentDate}.' },
  'journey.reviewOrder': { zh: '查看并下单', en: 'Review & order' },
  'journey.reviewYourInquiry': { zh: '确认询价信息', en: 'Review your inquiry' },
  'journey.seeWhatEachOfferIncludes': { zh: '每份报价包含什么，一目了然', en: 'See what each offer includes' },
  'journey.shipDate': { zh: '期望出运', en: 'Ship date' },
  'journey.smallerBagsInsideCarton': { zh: '箱内分装小袋', en: 'Smaller bags inside carton' },
  'journey.specificationPending': { zh: '品种/规格待确认', en: 'specification pending' },
  'journey.standardOuterPackagingNoCustomizatio': { zh: '按常规外包装，无定制要求', en: 'Standard outer packaging; no customization' },
  'journey.standardPackaging': { zh: '按常规包装', en: 'Standard packaging' },
  'journey.status.ORDERED': { zh: '已下单', en: 'Ordered' },
  'journey.status.PENDING': { zh: '待受理', en: 'Received' },
  'journey.status.PROCESSING': { zh: '处理中', en: 'In preparation' },
  'journey.status.QUOTED': { zh: '已报价', en: 'Quoted' },
  'journey.status.WITHDRAWN': { zh: '已撤回', en: 'Withdrawn' },
  'journey.tellMeWhatElseWe': { zh: '请告诉我还有哪些要求需要纳入报价；暂时没有定也可以之后补充。', en: 'Tell me what else we should include. You can also leave it for later.' },
  'journey.tellUsOnce': { zh: '说一次就够', en: 'Tell us once' },
  'journey.theCountsAddUpTo': { zh: '目前合计 {sum} 箱；按总量和每箱净重推算约为 {v2} 箱。请调整各包装箱数后再确认。', en: 'The counts add up to {sum} cartons, while your requested quantity is about {v} cartons. Please adjust the split.' },
  'journey.thisDateMayBeToo': { zh: '这个日期可能赶不上。按目前可用航线估算，最早约 {date} 到货（航程 {sailingDays} 天 + 7 天履约缓冲）。', en: 'This date may be too soon. Based on current route data, the earliest estimated arrival is {date} ({sailingDays} sailing days plus 7 days for handling).' },
  'journey.ton': { zh: '吨', en: 'ton' },
  'journey.transport': { zh: '运输偏好', en: 'Transport' },
  'journey.uploading': { zh: '上传中', en: 'Uploading' },
  'journey.useEarliestDate': { zh: '采用最早可达日期', en: 'Use earliest date' },
  'journey.useForm': { zh: '改用表单填写', en: 'Use form' },
  'journey.vOfStagescount': { zh: '第 {v} 步 / 共 {stagesCount} 步', en: '{v} of {stagesCount}' },
  'journey.valuePerCarton': { zh: '每箱 {value}', en: '{value} per carton' },
  'journey.varietySpecificationNotSelected': { zh: '品种/规格暂不确定', en: 'Variety/specification not selected' },
  'journey.verifiedTrade': { zh: '可信跨境交易', en: 'Verified trade' },
  'journey.viewConversation': { zh: '查看沟通记录', en: 'View conversation' },
  'journey.viewOrderDetails': { zh: '查看订单详情', en: 'View order details' },
  'journey.weNoLongerAskYou': { zh: '这里不再要求您选择具体承运航线。您可以告诉我们运输偏好，实际航线会在报价时确认。', en: 'We no longer ask you to select a specific carrier route here. Please share a delivery preference; the confirmed route will be shown with your quote.' },
  'journey.weekdays': { zh: '一,二,三,四,五,六,日', en: 'M,T,W,T,F,S,S' },
  'journey.whichPackagingMaterialWouldYou': { zh: '大包装您希望用哪种包装？', en: 'Which packaging material would you like for the bulk pack?' },
  'journey.wovenBag': { zh: '编织袋', en: 'Woven bag' },
  'journey.yesIHaveCustomRequirements': { zh: '有定制要求', en: 'Yes, I have custom requirements' },
  'journey.youStayInControl': { zh: '每一步由你确认', en: 'You stay in control' },
  'journey.yourDetailsAreSavedAs': { zh: '需求信息已整理', en: 'Your details are saved as we go' },
  // ---- journeyCopy ----
  'journeyCopy.acceptOrder': { zh: '接受报价并下单', en: 'Accept quote & place order' },
  'journeyCopy.accepting': { zh: '处理中…', en: 'Processing…' },
  'journeyCopy.ack': { zh: '收到了。我会边聊边帮你整理需求，缺什么就问你。信息齐了点「提交询价」就行。', en: 'Got it. I’ll organize your request as we chat and only ask for what’s missing. When it’s complete, just tap “Submit inquiry”.' },
  'journeyCopy.adjust': { zh: '有疑问？回到这条询盘沟通', en: 'Ask a question in this inquiry' },
  'journeyCopy.askAdditionalRequirements': { zh: '还有认证、检验或其他特殊要求吗？没有的话可以直接继续。', en: 'Do you need any certificates, inspections or other special requirements? Otherwise, you can continue.' },
  'journeyCopy.askCustomPack': { zh: '包装需要定制尺寸、品牌印刷或唛头吗？可以先描述；暂时没有定稿也可以选“稍后补充”，先继续询价。', en: 'Do you need custom dimensions, a logo, printed artwork or carton marks? You can describe them now; if they are not ready, note that they will follow.' },
  'journeyCopy.askDeliveryDate': { zh: '希望什么时候到货？可以选一个日期、尽快安排，或告诉我们时间灵活。', en: 'When would you like the order to arrive? Pick a date, ask for the earliest possible delivery, or tell us your timing is flexible.' },
  'journeyCopy.askInnerPack': { zh: '每箱里面您倾向哪种内包装？选一种即可；想比较常见方案，也可以点“比较常见包装方案”。', en: 'Which inner pack do you prefer for each carton? Choose one, or ask us to compare common options.' },
  'journeyCopy.askNetWeight': { zh: '您倾向用哪种外包装？不确定时可以按常规出口纸箱报价。', en: 'Which outer package do you prefer? We can quote a standard export carton if you are unsure.' },
  'journeyCopy.askOuterPack': { zh: '每箱外包装净重希望是多少？', en: 'What net weight should each outer carton hold?' },
  'journeyCopy.askOuterPackaging': { zh: '外包装您倾向哪种？是否适合会结合商品特性和冷链方式确认。', en: 'Which outer packaging do you prefer? The suitable options depend on the product and cold-chain plan.' },
  'journeyCopy.askPackAllocation': { zh: '这些包装方案是希望本单混装，还是先分别报价、看价格后再决定？', en: 'Would you like these configurations mixed in one order, or quoted separately so you can decide later?' },
  'journeyCopy.askPackAllocationDetails': { zh: '请分别填写每种包装规格需要多少箱，并让合计箱数对应本次总量。', en: 'Enter the number of cartons for each selected configuration. We will check that the total matches your request.' },
  'journeyCopy.askSpec': { zh: '您要哪种品种和规格？可以点选下面的选项，也可以在输入框里补充其他规格。暂时不确定也可以跳过。', en: 'Which variety and grade/specification would you like? Choose a listed option or type another one below.' },
  'journeyCopy.askTerm': { zh: '您倾向用哪种贸易术语报价？可以选 FOB、CFR、CIF，也可以让平台提供可选方案。', en: 'Which trade term would you like quoted? Choose FOB, CFR or CIF, or ask us to compare the available terms.' },
  'journeyCopy.askTransport': { zh: '运输方面有偏好吗？这能帮助我们准备合适方案；实际航线会在报价时确认。', en: 'Do you have a delivery preference? This helps us prepare the right options; the actual route will be confirmed with your quote.' },
  'journeyCopy.assistant': { zh: '蓝宝', en: 'Blu' },
  'journeyCopy.chooseDate': { zh: '选择日期', en: 'Choose date' },
  'journeyCopy.close': { zh: '关闭', en: 'Close' },
  'journeyCopy.compareStandardPack': { zh: '比较常见包装方案', en: 'Compare common packaging options' },
  'journeyCopy.confirm': { zh: '提交询价', en: 'Submit inquiry' },
  'journeyCopy.conversation': { zh: '询价对话', en: 'Your sourcing request' },
  'journeyCopy.dateAsap': { zh: '尽快安排', en: 'As soon as possible' },
  'journeyCopy.dateFlexible': { zh: '时间灵活', en: 'Timing is flexible' },
  'journeyCopy.dateLabel': { zh: '期望到货', en: 'Need by' },
  'journeyCopy.delivery': { zh: '期望到货', en: 'Need by' },
  'journeyCopy.destCountryLabel': { zh: '目的国', en: 'Destination country' },
  'journeyCopy.destPortLabel': { zh: '目的港（选填）', en: 'Port (optional)' },
  'journeyCopy.destination': { zh: '目的地', en: 'Destination' },
  'journeyCopy.doing': { zh: '正在准备报价方案', en: 'Preparing your quote' },
  'journeyCopy.done': { zh: '完成', en: 'Done' },
  'journeyCopy.edit': { zh: '修改信息', en: 'Edit details' },
  'journeyCopy.eyebrow': { zh: '开始询价', en: 'REQUEST A QUOTE' },
  'journeyCopy.greeting': { zh: '你好！说说你需要什么，我来帮你整理询价信息。', en: 'Hi! Tell me what you need, and I’ll help organize the details for your quote request.' },
  'journeyCopy.inquiryNo': { zh: '询盘编号', en: 'Inquiry no.' },
  'journeyCopy.live': { zh: '需求信息已整理', en: 'REQUEST DETAILS' },
  'journeyCopy.login': { zh: '去登录', en: 'Sign in' },
  'journeyCopy.loginFirst': { zh: '提交询价需要先登录', en: 'Please sign in to submit an inquiry' },
  'journeyCopy.networkError': { zh: '网络异常，请稍后重试。', en: 'Something went wrong. Please try again.' },
  'journeyCopy.notNow': { zh: '暂不', en: 'Not now' },
  'journeyCopy.openFull': { zh: '查看完整报价与费用明细', en: 'View full quote & cost breakdown' },
  'journeyCopy.optional': { zh: '选填', en: 'Optional' },
  'journeyCopy.orderBody': { zh: '订单已创建。在订单详情页可以跟踪每个履约节点，并查看实时冷链温度。', en: 'Your order has been created. Track every milestone and the live cold-chain temperature on the order page.' },
  'journeyCopy.orderTitle': { zh: '订单已确认', en: 'Order confirmed' },
  'journeyCopy.packCompare': { zh: '先分别报价，之后再决定', en: 'Quote each configuration separately; I will decide later' },
  'journeyCopy.packComplete': { zh: '包装偏好已记下。下面可以核对完整询价信息，需要的话还能继续补充。', en: 'Packaging preference noted. Review the details below before submitting.' },
  'journeyCopy.packDescribe': { zh: '有，我来补充要求', en: 'I have requirements; I will describe them' },
  'journeyCopy.packLater': { zh: '稍后补充', en: 'I will provide details later' },
  'journeyCopy.packMixed': { zh: '本单混装', en: 'Mix them in this order' },
  'journeyCopy.packNoRequirement': { zh: '没有其他要求', en: 'No other requirements' },
  'journeyCopy.packOther': { zh: '其他，请补充', en: 'Other / describe it' },
  'journeyCopy.packSkip': { zh: '暂不确定', en: 'Not sure yet' },
  'journeyCopy.packaging': { zh: '包装', en: 'Packaging' },
  'journeyCopy.packagingLabel': { zh: '包装备注（选填）', en: 'Packaging notes (optional)' },
  'journeyCopy.parseFail': { zh: '刚才这条没处理成功，请换个说法再说一次（如「20 吨」）。', en: 'Sorry, I couldn’t process that last message. Could you rephrase it (e.g. “20 tons”)?' },
  'journeyCopy.pending': { zh: '待补充', en: 'Tap to add' },
  'journeyCopy.placeholder': { zh: '例如：我需要 20 吨大白菜，11 月前送到迪拜……', en: 'e.g. I need 20 tons of Chinese cabbage delivered to Dubai by November…' },
  'journeyCopy.privacy': { zh: '提交前，你可以检查并修改需求信息。', en: 'Your request is only submitted after you review and confirm it.' },
  'journeyCopy.product': { zh: '商品', en: 'Product' },
  'journeyCopy.productPick': { zh: '搜索商品…', en: 'Search product…' },
  'journeyCopy.productRequired': { zh: '请填写商品名称或描述', en: 'Enter a product name or description' },
  'journeyCopy.progress': { zh: '补齐关键信息，核对后提交询价。', en: 'Add key details, review, then submit.' },
  'journeyCopy.qtyLabel': { zh: '数量', en: 'Quantity' },
  'journeyCopy.quantity': { zh: '数量', en: 'Quantity' },
  'journeyCopy.quoteNo': { zh: '报价编号', en: 'Quote no.' },
  'journeyCopy.quoteReady': { zh: '报价已准备好', en: 'Your quote is ready' },
  'journeyCopy.quoteSub': { zh: '蓝星平台出具 · 下单前请核对', en: 'Quoted by Bluplanet · review before ordering' },
  'journeyCopy.quoteTitle': { zh: '报价详情', en: 'Quote details' },
  'journeyCopy.quoteWaiting': { zh: '我们正在为你准备报价。报价准备好后会显示在这里，你可以稍后回来查看。', en: 'We are preparing your quote. You will see it here once ready — feel free to check back later.' },
  'journeyCopy.ready': { zh: '询价信息已整理好，请检查', en: 'Request details are ready to review' },
  'journeyCopy.readyNote': { zh: '需求已齐，核对后点「提交询价」。', en: 'Looks complete — review the card and tap “Submit inquiry”.' },
  'journeyCopy.refine': { zh: '提交前，你可以检查并修改每一项信息。', en: 'You can review and edit every detail before submitting your request.' },
  'journeyCopy.refresh': { zh: '刷新状态', en: 'Refresh' },
  'journeyCopy.sample': { zh: '我需要 20 吨大白菜，11 月前送到迪拜。', en: 'I need about 20 tons of Chinese cabbage, delivered to Dubai before November.' },
  'journeyCopy.send': { zh: '发送', en: 'Send' },
  'journeyCopy.specLabel': { zh: '规格', en: 'Specification' },
  'journeyCopy.start': { zh: '开始询价', en: 'Start inquiry' },
  'journeyCopy.step1': { zh: '说需求', en: 'Describe need' },
  'journeyCopy.step2': { zh: '补细节', en: 'Add details' },
  'journeyCopy.step3': { zh: '询价已提交', en: 'Inquiry sent' },
  'journeyCopy.step4': { zh: '查看报价', en: 'Review quote' },
  'journeyCopy.step5': { zh: '确认下单', en: 'Place order' },
  'journeyCopy.submitting': { zh: '提交中…', en: 'Submitting…' },
  'journeyCopy.subtitle': { zh: '补充商品、数量和交付信息，获取报价方案。看清价格与交付条件后，再决定是否下单。', en: 'Describe what you need. Review the price and delivery terms before deciding whether to place an order.' },
  'journeyCopy.summary': { zh: '询价内容', en: 'REQUEST DETAILS' },
  'journeyCopy.termCompare': { zh: '暂不确定，请提供 FOB、CFR、CIF 等方案供我比较', en: 'Not sure — please compare FOB, CFR and CIF options' },
  'journeyCopy.termLabel': { zh: '贸易条款', en: 'Trade term' },
  'journeyCopy.termRecorded': { zh: '贸易术语偏好已记下。下面可以核对完整询价信息，需要的话还能继续补充。', en: 'Trade term preference noted. Review the details below before submitting.' },
  'journeyCopy.terms': { zh: '30% 订金 · 70% 凭单付款', en: '30% deposit · 70% against documents' },
  'journeyCopy.title': { zh: '说说你需要什么', en: 'Tell us what you need' },
  'journeyCopy.total': { zh: '总价', en: 'Total' },
  'journeyCopy.transportCost': { zh: '费用优先', en: 'Cost matters most' },
  'journeyCopy.transportFast': { zh: '尽量快，优先考虑时效', en: 'Prefer the fastest reasonable delivery' },
  'journeyCopy.transportFewTransfers': { zh: '尽量少中转', en: 'Prefer fewer transfers' },
  'journeyCopy.transportNone': { zh: '暂无偏好，请平台安排合适方案', en: 'No preference — please arrange the best option' },
  'journeyCopy.transportRecorded': { zh: '收到，我会把它作为运输偏好；实际航线、时效和费用会在报价时确认。', en: 'Got it. We’ll treat this as a preference only; the confirmed route, timing and costs will be shown with your quote.' },
  'journeyCopy.trust': { zh: '每一步由你确认', en: 'You stay in control' },
  'journeyCopy.trustBody': { zh: '检查并确认后，询价信息才会提交。', en: 'Nothing is sent until you review and confirm.' },
  'journeyCopy.unitPrice': { zh: '单价', en: 'Unit price' },
  'journeyCopy.useEarliestDate': { zh: '采用最早可达日期', en: 'Use earliest estimate' },
  'journeyCopy.verified': { zh: '核对价格、交付和付款条件', en: 'Review price, delivery and payment terms' },
  'journeyCopy.viewInquiries': { zh: '我的询盘', en: 'My inquiries' },
  'journeyCopy.viewOrder': { zh: '查看订单详情与实时温度', en: 'View order details & live temperature' },
  'journeyCopy.viewOrders': { zh: '我的订单', en: 'My orders' },
  'journeyCopy.you': { zh: '你', en: 'You' },
  // ---- lang ----
  'lang.switch': { zh: '切换语言', en: 'Change language' },
  // ---- message ----
  'message.content.INQUIRY_REPLY': { zh: '询盘 {inquiryNo} 已回复报价单 {quoteNo} ，请前往「我的询盘」查看详情并确认下单。', en: 'Inquiry {inquiryNo} has been answered with quotation {quoteNo}. Go to "My Inquiries" to review details and place an order.' },
  'message.content.INQUIRY_SUBMITTED': { zh: '您的询盘 {inquiryNo} 已提交（共 {count} 个商品行），{rest}', en: 'Your inquiry {inquiryNo} has been submitted with {count} item line(s). Our trade advisor will reply with a formal quote shortly.' },
  'message.content.ORDER_CREATED': { zh: '报价单 {quoteNo} 已确认并生成订单 {orderNo} ，请前往「我的订单」查看。', en: 'Quotation {quoteNo} has been confirmed and converted to order {orderNo}. See "My Orders" for details.' },
  'message.content.QUOTE_ACCEPTED': { zh: '您已确认报价单 {quoteNo} ，可前往「我的询盘」转订单。', en: 'You have confirmed quotation {quoteNo}. Go to "My Inquiries" to convert it into an order.' },
  // ---- nav ----
  'nav.requestAQuote': { zh: '发起询盘', en: 'Request a quote' },
  // ---- product ----
  'product.iDLikeAQuote': { zh: '我想询价：{productName}{v2}', en: 'I’d like a quote for {productName}{v}.' },
  // ---- profile ----
  'profile.descForce': { zh: '发布询价前请先完善企业信息，便于买家与供应商识别您的企业。', en: 'Please complete your company profile before requesting a quote, so suppliers can identify your business.' },
  // ---- progress ----
  'progress.addAttachment': { zh: '添加附件', en: 'Add attachment' },
  'progress.advisor': { zh: '顾', en: 'Advisor' },
  'progress.attachJpgPngPdfDoc': { zh: '支持 JPG、PNG、PDF、DOC、XLS 文件。', en: 'Attach JPG, PNG, PDF, DOC or XLS files.' },
  'progress.availableQuoteOptions': { zh: '可选报价方案（{v}）', en: 'Available quote options' },
  'progress.backToInquiries': { zh: '返回我的询盘', en: 'Back to inquiries' },
  'progress.blu': { zh: '蓝', en: 'Blu' },
  'progress.conversationUpdates': { zh: '沟通记录与进度', en: 'Conversation & updates' },
  'progress.couldNotLoadThisInquiry': { zh: '询盘加载失败，请稍后重试。', en: 'Could not load this inquiry.' },
  'progress.couldNotStartVoiceRecognition': { zh: '无法启动语音识别，请稍后重试。', en: 'Could not start voice recognition.' },
  'progress.eachFileMustBe10': { zh: '单个文件不能超过 10 MB。', en: 'Each file must be 10 MB or smaller.' },
  'progress.goodsTotal': { zh: '商品货款', en: 'Goods total' },
  'progress.inquiryNotFound': { zh: '没有找到这条询盘。', en: 'Inquiry not found.' },
  'progress.loading': { zh: '附件加载中…', en: 'Loading…' },
  'progress.messageNotSentTryAgain': { zh: '留言发送失败，请重试。', en: 'Message not sent. Try again.' },
  'progress.messageYourAdvisor': { zh: '给顾问留言…', en: 'Message your advisor…' },
  'progress.myInquiries': { zh: '我的询盘', en: 'My inquiries' },
  'progress.needBy': { zh: '期望到货', en: 'Need by' },
  'progress.noMessagesYet': { zh: '暂时没有沟通记录。', en: 'No messages yet.' },
  'progress.onlyTheFirstFilesWere': { zh: '附件最多 6 个，已添加前 6 个。', en: 'Only the first files were added; the limit is 6.' },
  'progress.packaging': { zh: '包装/备注', en: 'Packaging' },
  'progress.pressAndHoldToSpeak': { zh: '按住说话', en: 'Press and hold to speak' },
  'progress.previewAttachment': { zh: '预览附件', en: 'Preview attachment' },
  'progress.quoteDetailsAreBeingUpdated': { zh: '报价方案正在更新，请稍后刷新。', en: 'Quote details are being updated. Refresh shortly.' },
  'progress.quoteOption': { zh: '报价方案', en: 'Quote option' },
  'progress.quoteRequest': { zh: '商品询价', en: 'Quote request' },
  'progress.recognizing': { zh: '识别中', en: 'Recognizing' },
  'progress.referenceSailingTimeAboutSailingdays': { zh: '参考航行约 {sailingDays} 天', en: 'Reference sailing time: about {sailingDays} days' },
  'progress.releaseToFinish': { zh: '松开结束', en: 'Release to finish' },
  'progress.releaseToFinishSpeaking': { zh: '松开结束语音输入', en: 'Release to finish speaking' },
  'progress.requestDetails': { zh: '本次询价需求', en: 'Request details' },
  'progress.reviewThisOption': { zh: '查看方案详情', en: 'Review this option' },
  'progress.sendMessage': { zh: '发送留言', en: 'Send message' },
  'progress.status.ORDERED': { zh: '已下单', en: 'Ordered' },
  'progress.status.PENDING': { zh: '待受理', en: 'Pending' },
  'progress.status.PROCESSING': { zh: '处理中', en: 'Processing' },
  'progress.status.QUOTED': { zh: '已报价', en: 'Quoted' },
  'progress.status.WITHDRAWN': { zh: '已撤回', en: 'Withdrawn' },
  'progress.step1': { zh: '说需求', en: 'Describe need' },
  'progress.step2': { zh: '确认提交', en: 'Review & submit' },
  'progress.step3': { zh: '询盘处理中', en: 'Inquiry received' },
  'progress.step4': { zh: '查看报价', en: 'Quote ready' },
  'progress.step5': { zh: '确认下单', en: 'Order' },
  'progress.termScope.CFR': { zh: '报价包含至目的港的国际运费', en: 'Includes international freight to the named destination port' },
  'progress.termScope.CIF': { zh: '报价包含至目的港的国际运费和保险', en: 'Includes international freight and insurance to the named destination port' },
  'progress.termScope.DDP': { zh: '请查看报价明细中的进口清关、税费和派送范围', en: 'See quote details for import clearance, duties and delivery scope' },
  'progress.termScope.EXW': { zh: '工厂交货；提货后的运输及费用由买家安排', en: 'Ex works; the buyer arranges collection and onward costs' },
  'progress.termScope.FOB': { zh: '报价至约定装运港；国际运费和保险另行安排', en: 'Quoted to the named loading port; main freight and insurance arranged separately' },
  'progress.text': { zh: '、', en: ', ' },
  'progress.thisMessageWillBeAdded': { zh: '留言会保存在这条询盘下，顾问可在后台查看并回复。', en: 'This message will be added to this inquiry.' },
  'progress.tradeTerm': { zh: '贸易条款', en: 'Trade term' },
  'progress.upTo6FilesCan': { zh: '最多同时添加 6 个文件。', en: 'Up to 6 files can be attached.' },
  'progress.validValiddaysDays': { zh: '有效 {validDays} 天', en: 'Valid {validDays} days' },
  'progress.view5StepProgress': { zh: '查看五步进度', en: 'View 5-step progress' },
  'progress.voiceRecognitionFailedPleaseTry': { zh: '语音识别没有成功，请重试或直接输入。', en: 'Voice recognition failed. Please try again or type.' },
  'progress.voiceTypingIsNotAvailable': { zh: '当前浏览器暂不支持语音转文字，请直接输入。', en: 'Voice typing is not available in this browser.' },
  'progress.you': { zh: '我', en: 'You' },
  // ---- quote ----
  'quote.backToQuoteOptions': { zh: '返回报价方案', en: 'Back to quote options' },
  'quote.cartonRemainder': { zh: '（余 {kg}kg）', en: ' ({kg} kg remaining)' },
  // ---- quoteUnit ----
  'quoteUnit.CASE': { zh: '件', en: 'cases' },
  'quoteUnit.KG': { zh: 'kg', en: 'kg' },
  'quoteUnit.PIECE': { zh: '个', en: 'pcs' },
  'quoteUnit.TON': { zh: '吨', en: 'tons' },
  // ---- service ----
  'service.endToEndSupportFor': { zh: '为您的全球农产品贸易提供全流程支持。', en: 'End-to-end support for your global agricultural trade.' },
  // ---- unit ----
  'unit.case': { zh: '箱', en: 'cases' },
  'unit.kg': { zh: '千克', en: 'kg' },
  'unit.piece': { zh: '件', en: 'pcs' },
  'unit.ton': { zh: '吨', en: 'tons' },
} satisfies Record<string, Entry>;

export type DictKey = keyof typeof ENTRIES;

export const DICT: Record<string, Entry> = ENTRIES;
